// src/app/login/login.component.ts
import { Component } from '@angular/core';
import { AuthService } from '../core/auth.service';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';

type AuthView = 'login' | 'signup' | 'forgot_password';

// Validador personalizado para confirmar que las contraseñas coinciden
export function passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
  const password = control.get('password');
  const confirmPassword = control.get('confirmPassword');

  if (password && confirmPassword && password.value !== confirmPassword.value) {
    return { passwordMismatch: true };
  }
  return null;
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent {
  currentView: AuthView = 'login';
  
  loginForm: FormGroup;
  signupForm: FormGroup;
  forgotPasswordForm: FormGroup;

  isLoading = false;
  authError: string | null = null;
  authSuccessMessage: string | null = null;

  constructor(
    private authService: AuthService,
    private fb: FormBuilder
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });

    this.signupForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required]
    }, { validators: passwordMatchValidator }); // Aplicar el validador a nivel de formulario

    this.forgotPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  changeView(view: AuthView) {
    this.currentView = view;
    this.authError = null;
    this.authSuccessMessage = null;
    this.loginForm.reset();
    this.signupForm.reset();
    this.forgotPasswordForm.reset();
  }

  async onLogin() {
    if (this.loginForm.invalid) {
      return;
    }
    this.isLoading = true;
    this.authError = null;
    this.authSuccessMessage = null;
    const { email, password } = this.loginForm.value;

    try {
      await this.authService.login(email, password);
      // La navegación ahora la maneja el AuthService
    } catch (error: any) {
      this.authError = this.getFriendlyErrorMessage(error);
    } finally {
      this.isLoading = false;
    }
  }

  async onSignup() {
    if (this.signupForm.invalid) {
      return;
    }
    this.isLoading = true;
    this.authError = null;
    this.authSuccessMessage = null;
    const { email, password } = this.signupForm.value;

    try {
      await this.authService.signup(email, password);
      this.changeView('login'); // Cambiar la vista primero
      this.authSuccessMessage = "¡Cuenta creada exitosamente! Por favor, inicie sesión."; // Luego establecer el mensaje
    } catch (error: any) {
      this.authError = this.getFriendlyErrorMessage(error);
    } finally {
      this.isLoading = false;
    }
  }

  async onForgotPassword() {
    if (this.forgotPasswordForm.invalid) {
      return;
    }
    this.isLoading = true;
    this.authError = null;
    this.authSuccessMessage = null;
    const { email } = this.forgotPasswordForm.value;

    try {
      await this.authService.forgotPassword(email);
      this.authSuccessMessage = "Se ha enviado un enlace para restablecer su contraseña a su correo electrónico.";
    } catch (error: any) {
      this.authError = this.getFriendlyErrorMessage(error);
    } finally {
      this.isLoading = false;
    }
  }

  private getFriendlyErrorMessage(error: any): string {
      switch (error?.code) {
          case 'auth/invalid-email':
              return 'Formato de correo inválido.';
          case 'auth/user-not-found':
          case 'auth/wrong-password':
          case 'auth/invalid-credential':
              return 'Correo o contraseña inválidos.';
          case 'auth/too-many-requests':
              return 'Demasiados intentos. Por favor intente más tarde.';
          case 'auth/email-already-in-use':
              return 'Este correo electrónico ya está en uso.';
          case 'auth/user-not-found-in-system':
              return 'Este usuario no está registrado en el sistema.';
          default:
              return error?.message || 'Ocurrió un error inesperado. Por favor intente de nuevo.';
      }
  }
}